import React, { useState } from "react";
import { X, ChevronDown } from "lucide-react";

interface TermsDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function TermsDialog({ open, onClose }: TermsDialogProps) {
  if (!open) return null;

  return (
    <div className="lb-dialog fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onMouseDown={onClose} />

      <div
        className="lb-dialog-box relative z-10 w-full max-w-4xl max-h-[85vh] overflow-y-auto lb-scroll"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="lb-dialog-bar sticky top-0 z-10 flex items-center justify-between px-8 py-5">
          <div>
            <h2 className="lb-dialog-title">
              Termos de Uso, Regras e Politica de Compras
            </h2>
            <p className="text-sm text-white/40 mt-1">
              Cabal Liberty EP39 — Liberty Games LTDA — CNPJ 08.778.122/0001-15
            </p>
          </div>
          <button
            onClick={onClose}
            className="lb-dialog-x"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-6 text-sm text-white/65 leading-relaxed">
          <Alert type="warning">
            <strong>LEIA ATENTAMENTE ANTES DE ACESSAR O SERVICO.</strong> Ao criar uma conta e/ou utilizar qualquer funcionalidade do Servico, o Usuario declara ter lido, compreendido e aceito integralmente todos os termos e condicoes deste instrumento.
          </Alert>

          {/* Identificacao */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <MetaCard label="Razao Social" value="Liberty Games LTDA" />
            <MetaCard label="CNPJ" value="08.778.122/0001-15" mono />
            <MetaCard label="Site Oficial" value="www.caballiberty.com" link="https://www.caballiberty.com" />
            <MetaCard label="Endereco" value="Av. Eng. Leal Lima Verde, 1100 — Loja 02, Sapiranga/Coite, Fortaleza-CE" />
            <MetaCard label="Assessoria Juridica" value="Jafe Advogados Associados" />
            <MetaCard label="Vigencia" value="30/03/2026 — Ate nova publicacao" mono />
          </div>

          {/* SECAO 1 */}
          <Section num="01" title="Termos de Uso">
            <p>A Liberty Games LTDA, pessoa juridica de direito privado, inscrita no CNPJ sob o n. <strong>08.778.122/0001-15</strong>, com sede na Av. Engenheiro Leal Lima Verde, 1100, Loja 02, Sapiranga/Coite, Fortaleza — CE, CEP 60833-175 (doravante denominada simplesmente "Liberty Games" ou "Empresa"), fornece o site caballiberty.com e todos os servicos a ele relacionados, incluindo o servidor privado de MMORPG <strong>Cabal Liberty EP39</strong> (coletivamente denominados "Servico"), sujeito ao cumprimento dos termos e condicoes estabelecidos neste instrumento ("Contrato").</p>
            <p className="mt-3">A Liberty Games reserva-se, a qualquer tempo, o direito de alterar os termos e condicoes deste Contrato, modificar o Servico, alterar valores de DONATES e modificar requisitos de hardware e software. Quaisquer alteracoes entrarao em vigor mediante notificacao publicada no Site. O uso continuado implica aceitacao tacita das alteracoes.</p>

            <SubSection title="1.1 — Registro e Requisitos de Acesso">
              <p>Para utilizar o Servico, o Usuario devera criar uma Conta. Ao concluir o registro, declara e garante:</p>
              <RuleList items={[
                "Ter 18 (dezoito) anos ou mais; ou, entre 13 e 18 anos, estar devidamente autorizado por responsavel legal;",
                "Ter plena capacidade civil para celebrar o presente Contrato;",
                "Ser pessoa fisica — e vedada a criacao de conta por pessoas juridicas;",
                "Fornecer informacoes veridicas, completas e atualizadas durante o cadastro.",
              ]} />
              <p className="mt-3">A Conta e de uso estritamente pessoal e intransferivel. O Usuario responsabiliza-se integralmente por todas as atividades realizadas mediante sua Conta. A Liberty Games nao se responsabiliza por acessos nao autorizados decorrentes de negligencia ou imprudencia do Usuario.</p>
            </SubSection>

            <SubSection title="1.2 — Processo de Registro e Informacoes Cadastrais">
              <p>Para o registro, o Usuario fornecera nome, e-mail e data de nascimento. A Liberty Games podera ainda coletar dados tecnicos (endereco de IP, hardware, sistema operacional) para fins de seguranca e suporte. As informacoes cadastrais serao mantidas em sigilo e nao compartilhadas com terceiros, salvo por determinacao judicial ou para operacoes de pagamento.</p>
              <p className="mt-3">A Liberty Games reserva-se o direito de suspender ou encerrar Contas com informacoes falsas, desatualizadas ou incompletas.</p>
            </SubSection>

            <SubSection title="1.3 — Nome de Usuario e Senha">
              <p>O Usuario escolhera um nome de usuario e senha para acesso. A Liberty Games podera recusar ou solicitar alteracao de nomes que:</p>
              <RuleList items={[
                "Usurpem identidade de terceiros, personagens do jogo, membros de Staff/GM ou celebridades;",
                "Sejam ofensivos, obscenos, discriminatorios ou de conotacao sexual;",
                "Contenham 'internetes', sequencias sem sentido ou expressoes relacionadas a atividades ilicitas;",
                "Incluam caracteres especiais nao permitidos pelo sistema;",
                "Violem marcas registradas, direitos de propriedade intelectual ou normas legais vigentes.",
              ]} />
              <Alert type="danger">
                <strong>ATENCAO:</strong> O Usuario e inteiramente responsavel pela confidencialidade de seu nome de usuario e senha, bem como por todas as atividades realizadas atraves de sua Conta.
              </Alert>
            </SubSection>
          </Section>

          {/* SECAO 2 */}
          <Section num="02" title="Regras de Conduta In-Game e na Comunidade">
            <p>A Liberty Games zela pelo fornecimento de um ambiente de jogo seguro, justo e agradavel para todos os Usuarios. Ao acessar o Servico, o Usuario compromete-se a respeitar integralmente as normas de conduta estabelecidas nesta Secao.</p>

            <SubSection title="2.1 — Principios Gerais de Conduta">
              <p>Sao expressamente proibidas as seguintes condutas:</p>
              <RuleList items={[
                "Restringir, impedir ou prejudicar a experiencia de jogo de outros Usuarios, inclusive por meio de programas ilegais;",
                "Realizar ataques a servidores ou tentativas de interrupcao dos sistemas;",
                "Utilizar o Servico para finalidade ilegal ou nao autorizada pela Liberty Games;",
                "Adotar comportamento rude, ofensivo, obsceno, ameacador, abusivo ou discriminatorio de qualquer natureza;",
                "Fingir ser membro de Staff, Game Master (GM) ou representante da Liberty Games;",
                "Transmitir virus, malware, trojans ou qualquer codigo de natureza destrutiva;",
                "Praticar spam, flooding ou envio massivo de mensagens automaticas;",
                "Compartilhar dados pessoais de outros Usuarios sem consentimento expresso;",
                "Divulgar links para sites externos nao autorizados pela Liberty Games;",
                "Realizar qualquer acao que comprometa a seguranca ou integridade do Servico.",
              ]} />
            </SubSection>

            <SubSection title="2.2 — Regras da Comunidade (Discord e Canais Oficiais)">
              <RuleList items={[
                "Respeito Mutuo: Comportamentos ofensivos, assedio ou qualquer forma de discriminacao nao serao tolerados;",
                "Ausencia de Spam: E vedado o envio de mensagens repetitivas, links nao solicitados ou divulgacoes nao autorizadas;",
                "Conteudo Apropriado: Todo conteudo publicado deve ser adequado para todas as idades;",
                "Privacidade: E vedado o compartilhamento de informacoes pessoais de outros membros;",
                "Canais Corretos: O Usuario deve publicar nos canais apropriados, observando a finalidade de cada um;",
                "Nome e Avatar: Devem ser adequados — a equipe de Staff pode solicitar alteracao de identificadores inapropriados;",
                "Termos das Plataformas: Os Termos de Servico das plataformas utilizadas (Discord, etc.) tambem devem ser observados.",
              ]} />
            </SubSection>

            <SubSection title="2.3 — Uso de Recursos Nao Autorizados">
              <p>E expressamente vedado ao Usuario, sob qualquer circunstancia:</p>
              <RuleList items={[
                "Utilizar hacks, cheats, bots, macros, softwares de auto farm, alteradores de memoria ou qualquer ferramenta nao autorizada;",
                "Realizar engenharia reversa, descompilar, desmontar ou modificar o cliente do jogo ou os servidores;",
                "Explorar bugs, falhas de design ou funcionalidades nao documentadas para obter vantagens competitivas;",
                "Utilizar maquinas virtuais para multiplicar contas ou simular jogadores adicionais;",
                "Criar, distribuir ou fornecer acesso a servidores privados, ferramentas de trapaca ou programas alteradores;",
                "Usar analisadores de rede (packet sniffers), scripts ou ferramentas de monitoramento do Servico.",
              ]} />
              <Alert type="danger">
                <strong>ATENCAO:</strong> O uso de recursos ilegais (hack, bot, cheat) e infracao <strong>GRAVISSIMA</strong> e resulta em banimento permanente imediato, sem possibilidade de recurso.
              </Alert>
            </SubSection>
          </Section>

          {/* SECAO 3 */}
          <Section num="03" title="Tabela Oficial de Punicoes">
            <p>As infracoes as normas estabelecidas neste Contrato sujeitam o Usuario as penalidades descritas abaixo, aplicadas de forma progressiva conforme a reincidencia e gravidade de cada caso.</p>

            <div className="overflow-x-auto mt-4">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-left text-white/50 uppercase tracking-wider">
                    <th className="py-2 pr-3">Infracao</th>
                    <th className="py-2 pr-3">Gravidade</th>
                    <th className="py-2 pr-3">1a</th>
                    <th className="py-2 pr-3">2a</th>
                    <th className="py-2">3a</th>
                  </tr>
                </thead>
                <tbody className="text-white/60">
                  <PunishmentRow infraction="Uso de recursos ilegais (hack, bot, cheat)" severity="Gravissima" first="Ban Permanente" />
                  <PunishmentRow infraction="Macro / Auto Farm em DG" severity="Gravissima" first="Ban Permanente" />
                  <PunishmentRow infraction="Abuso de BUG" severity="Grave" first="Ban 30 Dias" second="Ban Permanente" />
                  <PunishmentRow infraction="Comercio de itens irregulares" severity="Grave" first="Remocao + Aviso" second="Ban Permanente" />
                  <PunishmentRow infraction="Desrespeito a equipe de Staff" severity="Grave" first="Ban 15 Dias" second="Ban Permanente" />
                  <PunishmentRow infraction="Conduta inadequada" severity="Grave" first="Ban 30 Dias" second="Ban Permanente" />
                  <PunishmentRow infraction="Uso de maquina virtual" severity="Media" first="Aviso + Ban 3 Dias" second="Ban 30 Dias" third="Ban Permanente" />
                  <PunishmentRow infraction="Comercio por dinheiro real in-game" severity="Grave" first="Aviso Formal" second="Ban 15 Dias" third="Ban Permanente" />
                  <PunishmentRow infraction="Comportamento toxico" severity="Media" first="Ban 3 Dias" second="Ban 15 Dias" third="Ban Permanente" />
                </tbody>
              </table>
            </div>

            <Alert type="info">
              <strong>IMPORTANTE:</strong> As regras nao sao exaustivas. A tabela de punicoes esta sujeita a alteracoes. Situacoes nao previstas serao analisadas individualmente pela equipe de Staff.
            </Alert>
          </Section>

          {/* SECAO 4 */}
          <Section num="04" title="Politica de Compras e Itens Virtuais">
            <p>A Liberty Games disponibiliza uma loja virtual ("Loja de Itens") por meio da qual o Usuario podera adquirir moedas virtuais (<strong>CASH</strong> e/ou <strong>GEMAS</strong>) e itens virtuais ("Itens de Cash") para uso exclusivo no servidor Cabal Liberty EP39.</p>

            <SubSection title="4.1 — Natureza dos Itens Virtuais">
              <RuleList items={[
                "Os itens virtuais nao possuem valor monetario real e nao sao convertiveis em moeda corrente;",
                "O Usuario nao adquire qualquer direito de propriedade sobre itens virtuais ou moedas;",
                "A Liberty Games reserva-se o direito de modificar, limitar, remover ou excluir itens a qualquer tempo;",
                "O Usuario adquire apenas uma licenca limitada, nao exclusiva e revogavel para utilizacao dos itens dentro do Servico.",
              ]} />
            </SubSection>

            <SubSection title="4.2 — Condicoes de Compra e Politica de Nao Reembolso">
              <RuleList items={[
                "Toda e qualquer compra realizada e definitiva e irrevogavel. Apos confirmacao do pagamento, os valores nao serao reembolsados;",
                "Certos itens possuem prazo de validade — a vigencia de cada item sera informada no momento da compra;",
                "A Liberty Games nao se responsabiliza por tarifas bancarias ou taxas cobradas por instituicoes financeiras;",
                "Em caso de impossibilidade de processamento de pagamento, a Liberty Games podera suspender a Conta ate a regularizacao.",
              ]} />
              <Alert type="danger">
                <strong>ATENCAO:</strong> Uma vez autorizada a cobranca e confirmado o pagamento, nenhum valor sera reembolsado sob qualquer circunstancia, ainda que a Conta seja encerrada ou o Servico descontinuado.
              </Alert>
            </SubSection>

            <SubSection title="4.3 / 4.4 — Regras de Comercio de Itens Virtuais">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4">
                  <p className="text-green-400 font-bold text-xs mb-1">LIBERADO — OUT-GAME</p>
                  <p className="text-white/60 text-xs">E permitido vender, transferir ou negociar itens virtuais em plataformas externas (Mercado Livre, OLX, Facebook, WhatsApp) por valores em dinheiro real, fora do ambiente oficial do jogo.</p>
                </div>
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                  <p className="text-red-400 font-bold text-xs mb-1">PROIBIDO — IN-GAME</p>
                  <p className="text-white/60 text-xs">E estritamente proibido vender, negociar ou fazer propaganda de itens virtuais dentro do ambiente oficial do jogo por dinheiro real. O descumprimento implica nas sancoes da Tabela de Punicoes.</p>
                </div>
              </div>
            </SubSection>
          </Section>

          {/* SECAO 5 */}
          <Section num="05" title="Propriedade Intelectual">
            <p>O Servico e parte do seu conteudo — incluindo graficos, arte, musica, personagens, itens virtuais, logotipos e marcas — sao de propriedade da Liberty Games ou de seus licenciadores, protegidos pela legislacao brasileira, especialmente pela <strong>Lei n. 9.279/1996</strong> (Lei da Propriedade Industrial) e pela <strong>Lei n. 9.610/1998</strong> (Lei de Direitos Autorais).</p>
            <p className="mt-3">O Usuario recebe uma licenca limitada, nao exclusiva, intransferivel e revogavel para uso do Servico exclusivamente para fins pessoais e nao comerciais. E expressamente vedado ao Usuario:</p>
            <RuleList items={[
              "Reproduzir, distribuir, modificar, adaptar, criar obras derivadas ou exibir publicamente qualquer conteudo sem autorizacao previa e por escrito;",
              "Usar as marcas, logotipos ou elementos visuais da Liberty Games ou do Cabal Liberty sem autorizacao expressa;",
              "Enquadrar ou espelhar qualquer parte do Servico sem autorizacao previa e escrita.",
            ]} />
          </Section>

          {/* SECAO 6 */}
          <Section num="06" title="Privacidade e Protecao de Dados">
            <p>As informacoes coletadas dos Usuarios serao tratadas em conformidade com a <strong>Lei Geral de Protecao de Dados Pessoais — LGPD (Lei n. 13.709/2018)</strong> e demais normativas brasileiras aplicaveis.</p>
            <p className="mt-3">A Liberty Games podera coletar, sem aviso previo, informacoes tecnicas do dispositivo do Usuario (sistema operacional, endereco de IP, configuracoes de hardware) para fins de identificacao de infracoes, suporte tecnico e combate a trapacas.</p>
          </Section>

          {/* SECAO 7 */}
          <Section num="07" title="Rescisao, Suspensao e Descontinuacao">
            <SubSection title="7.1 — Rescisao pelo Usuario">
              <p>O Usuario podera encerrar sua Conta a qualquer momento, mediante solicitacao a equipe de suporte atraves dos canais oficiais. Apos a aceitacao do pedido, a Conta sera encerrada sem direito a reembolso.</p>
            </SubSection>
            <SubSection title="7.2 — Rescisao pela Liberty Games">
              <p>A Liberty Games reserva-se o direito de, com ou sem aviso previo, suspender ou encerrar a Conta do Usuario nas seguintes hipoteses:</p>
              <RuleList items={[
                "Violacao de qualquer disposicao deste Contrato;",
                "Pratica de infracoes previstas na Tabela de Punicoes;",
                "Fornecimento de informacoes falsas no cadastro;",
                "Determinacao a seu exclusivo criterio, para preservacao da integridade do Servico.",
              ]} />
            </SubSection>
            <SubSection title="7.3 — Descontinuacao do Servico">
              <p>A Liberty Games reserva-se o direito de interromper permanentemente o Servico a qualquer momento. Apos a descontinuacao, todas as Contas serao encerradas sem que isso gere qualquer obrigacao de reembolso.</p>
            </SubSection>
          </Section>

          {/* SECAO 8 */}
          <Section num="08" title="Limitacao de Responsabilidade e Isencoes">
            <div className="rounded-lg border border-white/10 bg-white/5 p-4 mb-3">
              <p className="text-xs text-white/50 uppercase font-bold mb-1">Isencao Geral</p>
              <p className="text-white/60 text-xs">O Servico e fornecido <strong>"no estado em que se encontra"</strong> e <strong>"conforme disponibilidade"</strong>, sem garantias de qualquer natureza, expressas ou implicitas, na maxima extensao permitida pela legislacao brasileira vigente.</p>
            </div>
            <p>A Liberty Games nao garante que o Servico sera ininterrupto, seguro ou isento de erros. Em nenhuma hipotese a Liberty Games sera responsavel por danos indiretos, incidentais, consequentes, especiais ou punitivos decorrentes do uso ou impossibilidade de uso do Servico.</p>
          </Section>

          {/* SECAO 9 */}
          <Section num="09" title="Disposicoes Gerais">
            <RuleList items={[
              "Legislacao Aplicavel: Este Contrato e regido pelas leis da Republica Federativa do Brasil.",
              "Foro Competente: Fica eleito o Foro da Comarca de Fortaleza, Estado do Ceara, com renuncia a qualquer outro foro.",
              "Independencia das Clausulas: A eventual invalidade de qualquer disposicao nao afetara a validade das demais clausulas.",
              "Nao Renuncia: A tolerancia da Liberty Games em relacao a qualquer infracao nao constituira novacao, renuncia ou precedente.",
              "Acordo Integral: O presente instrumento constitui o acordo integral entre o Usuario e a Liberty Games.",
            ]} />
          </Section>

          {/* FAQ */}
          <Section num="FAQ" title="Perguntas Frequentes">
            <FaqGroup title="Sobre a Conta">
              <FaqItem q="Posso ter mais de uma conta?" a="Cada Usuario pode ter apenas uma conta ativa. A criacao de multiplas contas pode resultar em banimento permanente de todas elas." />
              <FaqItem q="O que fazer se minha conta for hackeada?" a="Altere imediatamente sua senha e entre em contato com nossa equipe de suporte atraves do site oficial caballiberty.com. A Liberty Games envidara esforcos razoaveis para apurar o ocorrido." />
              <FaqItem q="Posso transferir minha conta para outra pessoa?" a="Nao. As contas sao pessoais e intransferiveis. A transferencia de conta e proibida e pode resultar em banimento permanente." />
            </FaqGroup>
            <FaqGroup title="Sobre Compras e Itens">
              <FaqItem q="As compras na Loja de Itens podem ser reembolsadas?" a="Nao. Todas as compras sao definitivas e nao reembolsaveis, conforme estabelecido na Secao 4 deste documento." />
              <FaqItem q="Posso vender meus itens por dinheiro real?" a="A venda fora do jogo (OUT-GAME) em plataformas externas e permitida. Porem, negociar por dinheiro real dentro do jogo (IN-GAME) e estritamente proibido." />
              <FaqItem q="Os itens virtuais tem validade?" a="Depende do item. Alguns possuem prazo de expiracao, enquanto outros sao permanentes. A informacao de validade e sempre exibida no momento da compra." />
            </FaqGroup>
            <FaqGroup title="Sobre Regras e Punicoes">
              <FaqItem q="O que acontece se eu usar hack ou bot?" a="O uso de recursos ilegais e infracao gravissima e resulta em banimento permanente imediato, sem possibilidade de recurso ou reembolso." />
              <FaqItem q="Posso recorrer de uma punicao aplicada pela Staff?" a="Sim. Recursos podem ser submetidos a equipe de moderacao atraves dos canais oficiais. A equipe de Staff tem a palavra final sobre todas as decisoes disciplinares." />
              <FaqItem q="Kill Stealing (KS) e permitido?" a="Sim, e permitido! Kill Stealing e considerado comportamento permitido no Cabal Liberty e nao esta sujeito a penalidades." />
            </FaqGroup>
            <FaqGroup title="Sobre o Servico">
              <FaqItem q="O servidor pode ficar offline? Serei reembolsado?" a="O Servico podera apresentar interrupcoes para manutencao ou por fatores tecnicos. Tais interrupcoes nao geram direito a reembolso. A empresa e responsavel por manter o servidor ativo e garantir o retorno a jogabilidade." />
              <FaqItem q="As regras podem ser alteradas?" a="Sim. A Liberty Games reserva-se o direito de alterar as regras a qualquer momento, mediante notificacao publicada no site oficial." />
              <FaqItem q="Como entro em contato com o suporte?" a="O suporte oficial pode ser acessado atraves do site caballiberty.com e dos canais oficiais da comunidade." />
            </FaqGroup>
          </Section>

          {/* DECLARACAO FINAL */}
          <Section num="FINAL" title="Declaracao Final e Aceite Digital">
            <Alert type="warning">
              <strong>Ao criar uma Conta e/ou utilizar qualquer funcionalidade do Servico, o Usuario declara ter lido, compreendido e aceito integralmente todos os termos e condicoes estabelecidos no presente instrumento</strong>, bem como todas as regras, politicas e comunicados publicados nos canais oficiais da Liberty Games.
              {"\n\n"}
              O nao cumprimento de qualquer das disposicoes deste Contrato podera resultar nas penalidades previstas na Tabela de Punicoes e/ou em medidas legais cabiveis, conforme a gravidade da infracao.
            </Alert>
          </Section>

          {/* Footer info */}
          <div className="text-center text-[10px] text-white/30 pt-4 border-t border-white/5 space-y-1">
            <p className="font-bold text-white/40">LIBERTY GAMES LTDA</p>
            <p>CNPJ 08.778.122/0001-15 — Av. Engenheiro Leal Lima Verde, 1100, Loja 02, Sapiranga/Coite — Fortaleza, CE</p>
            <p>Assessoria Juridica: Jafe Advogados Associados</p>
            <p>Documento vigente desde 30 de marco de 2026 — www.caballiberty.com</p>
          </div>
        </div>

        {/* Footer */}
        <div className="lb-dialog-bar bottom sticky bottom-0 px-8 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="btn btn-gold"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function Section({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="lb-dialog-rule mb-4" />
      <div className="flex items-center gap-3 mb-3">
        <span className="lb-dialog-num">
          {num}
        </span>
        <h3 className="lb-dialog-h">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h4 className="text-sm font-semibold text-white/70 mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function RuleList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2 text-white/60">
          <span className="lb-dialog-dot mt-0.5 shrink-0">&#x2022;</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Alert({ type, children }: { type: "warning" | "danger" | "info"; children: React.ReactNode }) {
  const styles = {
    warning: "border-yellow-500/30 bg-yellow-500/5 text-yellow-200/70",
    danger: "border-red-500/30 bg-red-500/5 text-red-200/70",
    info: "border-blue-500/30 bg-blue-500/5 text-blue-200/70",
  };
  return (
    <div className={`rounded-lg border p-4 text-xs mt-3 whitespace-pre-line ${styles[type]}`}>
      {children}
    </div>
  );
}

function MetaCard({ label, value, mono, link }: { label: string; value: string; mono?: boolean; link?: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <p className="text-[10px] font-mono text-white/40 uppercase tracking-wider mb-1">{label}</p>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline">{value}</a>
      ) : (
        <p className={`text-xs text-white/70 ${mono ? "font-mono" : ""}`}>{value}</p>
      )}
    </div>
  );
}

function PunishmentRow({ infraction, severity, first, second, third }: {
  infraction: string; severity: string; first: string; second?: string; third?: string;
}) {
  const severityColor = severity === "Gravissima" ? "text-red-400 bg-red-500/10 border-red-500/30"
    : severity === "Grave" ? "text-orange-400 bg-orange-500/10 border-orange-500/30"
    : "text-yellow-400 bg-yellow-500/10 border-yellow-500/30";
  const permClass = "text-red-400 font-bold";

  return (
    <tr className="border-b border-white/5">
      <td className="py-2 pr-3 font-medium text-white/70">{infraction}</td>
      <td className="py-2 pr-3">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${severityColor}`}>{severity}</span>
      </td>
      <td className={`py-2 pr-3 ${first.includes("Permanente") ? permClass : ""}`}>{first}</td>
      <td className={`py-2 pr-3 ${second?.includes("Permanente") ? permClass : "text-white/40"}`}>{second || "—"}</td>
      <td className={`py-2 ${third?.includes("Permanente") ? permClass : "text-white/40"}`}>{third || "—"}</td>
    </tr>
  );
}

function FaqGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-white/8 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-left text-white/70 hover:bg-white/5 transition cursor-pointer"
      >
        <span>{q}</span>
        <ChevronDown size={14} className={`shrink-0 ml-2 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 text-xs text-white/50">{a}</div>
      )}
    </div>
  );
}
